import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import { useNavigate } from 'react-router-dom';
import { baseUrlAdmin } from '../../store/apiConstants';
import Service from '../../services';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import BranchToSectionDD from '../BaseDropdownComponents/BranchToSectionDD';
import './DiscountContext.scss';

class DiscountContext extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        
        const fixedColumns = [
            { field: 'studentName', header: 'Student Name' },
            { field: 'admissionNo', header: 'Enrollment No' },
            { field: 'className', header: 'Class' }
        ];

        const metricColumns = [
            { field: 'baseFee', header: 'Base Fee Amount' },
            { field: 'discountPerc', header: 'Discount Given (%)' },
            { field: 'netFee', header: 'Net Fee Paid' },
            { field: 'classAvg', header: 'Class Avg (%)' },
            { field: 'schoolAvg', header: 'School Avg (%)' }
        ];

        const defaultSelectedMetrics = metricColumns.filter(col => 
            ['baseFee', 'discountPerc', 'netFee'].includes(col.field)
        );

        this.state = {
            loading: false,
            // Data from API (backend handles filtering)
            schoolAvg: 0,
            classMetrics: [],
            studentData: [],
            outlierCount: 0,

            // Filters & UI
            globalFilter: '',
            dropdownsData: {},
            filterKey: Date.now(), // Key to force BranchToSectionDD re-render
            fixedColumns: fixedColumns,
            metricColumns: metricColumns,
            selectedColumns: defaultSelectedMetrics,
            
            // Comparison Popup
            showAnalysisDialog: false,
            selectedStudent: null
        };
        
        this.toast = React.createRef();
        this.dt = React.createRef();
    }

    componentDidMount() {
        this.fetchDiscountData();
    }

    fetchDiscountData = async (filters = {}) => {
        this.setState({ loading: true });
        try {
            let orgId = this.props.userInfo?.orgId;
             if (!orgId) {
                try {
                    const storedUserInfo = JSON.parse(localStorage.getItem('userInfo'));
                    if (storedUserInfo?.orgId) orgId = storedUserInfo.orgId;
                } catch (e) {}
            }
            const academicYear = localStorage.getItem('userAcademicYear');
            
            let url = `${baseUrlAdmin}/fee-decision/discounts?academicYear=${academicYear}`;
            if(orgId) url += `&orgId=${orgId}`;
            
            // Add filter parameters to API call (check for valid non-empty values)
            const isValidFilter = (val) => val && val !== 'undefined' && val !== 'null' && val.trim() !== '';
            
            if (isValidFilter(filters.branchId)) url += `&branchId=${filters.branchId}`;
            if (isValidFilter(filters.boardId)) url += `&boardId=${filters.boardId}`;
            if (isValidFilter(filters.classId)) url += `&classId=${filters.classId}`;
            if (isValidFilter(filters.sectionId)) url += `&sectionId=${filters.sectionId}`;
            
            const response = await this.service.get(url, true);
            
            if (response && response.status && response.res && response.res.success) {
                const data = response.res.data || {};
                
                this.setState({
                    schoolAvg: data.schoolAvg || 0,
                    classMetrics: data.classMetrics || [],
                    studentData: data.studentData || [],
                    outlierCount: data.outlierCount || 0
                });
            } else {
                this.showToast('error', 'Error', 'Failed to fetch discount analysis');
            }
        } catch (error) {
            console.error('Error fetching discounts:', error);
            this.showToast('error', 'Error', 'Failed to fetch discount analysis');
        } finally {
            this.setState({ loading: false });
        }
    };

    setDropdownValues = (dropdownsData) => {
        this.setState({ dropdownsData }, () => {
            // Refetch data with new filters from backend
            this.fetchDiscountData(dropdownsData);
        });
    }

    clearAllFilters = () => {
        // Clear all filters and refetch all data
        this.setState({ 
            dropdownsData: {},
            filterKey: Date.now() // Change key to force BranchToSectionDD re-render
        }, () => {
            this.fetchDiscountData({});
            this.showToast('success', 'Filters Cleared', 'Showing all discount records');
        });
    }

    viewAnalysis = (student) => {
        this.setState({
            selectedStudent: student,
            showAnalysisDialog: true
        });
    }

    navigateToProfile = (studentId) => {
        if (this.props.navigate) {
            this.props.navigate(`/studentProfile?studentId=${studentId}`);
        } else {
            window.location.href = `/studentProfile?studentId=${studentId}`;
        }
    }

    navigateToLedger = (studentId) => {
        if (this.props.navigate) {
            this.props.navigate(`/financeNew/FeeLedger?studentId=${studentId}`);
        } else {
            window.location.href = '/financeNew/FeeLedger';
        }
    }

    actionBody = (rowData) => {
        return (
            <div className="flex gap-2">
                 <Authorizer permId={PERMISSIONS.FDE_DISCOUNT_DISPARITY}>
                    <Button 
                        icon="pi pi-chart-bar" 
                        className="p-button-text p-button-sm p-button-info" 
                        onClick={() => this.viewAnalysis(rowData)} 
                        tooltip="View Discount Analysis" 
                        tooltipOptions={{ position: 'left' }}
                    />
                 </Authorizer>
                 <Button 
                    icon="pi pi-user" 
                    className="p-button-text p-button-sm p-button-secondary" 
                    onClick={() => this.navigateToProfile(rowData.studentId)} 
                    tooltip="View Profile" 
                    tooltipOptions={{ position: 'left' }}
                />
            </div>
        );
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    onColumnToggle = (event) => {
        let selectedColumns = event.value;
        
        // If selection is empty (user deselected all), revert to defaults
        if (!selectedColumns || selectedColumns.length === 0) {
            selectedColumns = this.state.metricColumns.filter(col => 
                ['baseFee', 'discountPerc', 'netFee'].includes(col.field)
            );
        }

        let orderedSelectedColumns = this.state.metricColumns.filter(col => 
            selectedColumns.some(sCol => sCol.field === col.field)
        );
        this.setState({ selectedColumns: orderedSelectedColumns });
    }

    exportCSV = (selectionOnly) => {
        this.dt.current.exportCSV({ selectionOnly });
    };

    currencyBody = (rowData, field) => {
        const val = rowData[field];
        return val ? `₹${Number(val).toLocaleString('en-IN')}` : '₹0';
    };

    percentageBody = (rowData, field) => {
        const val = rowData[field];
        const formatted = `${val !== undefined && val !== null ? Number(val).toFixed(2) : 0}%`;
        
        if (field === 'discountPerc') {
            const isHigh = val > rowData.classAvg && (val - rowData.classAvg > 10); // Simple visual threshold
            return (
                <span className={rowData.isOutlier ? "text-red-500 font-bold" : (isHigh ? "text-orange-500 font-medium" : "")}>
                    {formatted} {rowData.isOutlier && <i className="pi pi-exclamation-circle ml-1" title="High Disparity Outlier"></i>}
                </span>
            );
        }
        return formatted;
    };
    
    renderHeader = () => {
        const { globalFilter, selectedColumns, metricColumns } = this.state;
        return (
            <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="flex align-items-center gap-2">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText 
                            type="search" 
                            value={globalFilter} 
                            onChange={(e) => this.setState({ globalFilter: e.target.value })} 
                            placeholder="Search..." 
                        />
                    </span>
                    <MultiSelect 
                        value={selectedColumns} 
                        options={metricColumns} 
                        optionLabel="header" 
                        onChange={this.onColumnToggle} 
                        style={{ width: '20em' }} 
                        placeholder="Select Columns"
                        display="chip"
                    />
                </div>
                <Authorizer permId={PERMISSIONS.FDE_EXPORT_DISCOUNT_ANALYSIS}>
                    <Button 
                        type="button" 
                        icon="pi pi-file-excel" 
                        label="Export" 
                        className="p-button-success" 
                        onClick={() => this.exportCSV(false)} 
                        data-pr-tooltip="CSV" 
                    />
                </Authorizer>
            </div>
        );
    }
    
    // Helper to render the comparison card content
    renderComparisonCard = (student) => {
        if (!student) return null;
        
        return (
             <div className="grid">
                 <div className="col-12 mb-3">
                    <div className="text-xl font-bold text-gray-800">{student.studentName}</div>
                    <div className="text-sm text-gray-500">{student.admissionNo} | {student.className}</div>
                 </div>
                 
                 <div className="col-12 md:col-4">
                     <div className="surface-100 p-3 border-round text-center h-full">
                         <div className="text-gray-600 mb-2">Student Discount</div>
                         <div className={`text-2xl font-bold ${student.isOutlier ? 'text-red-500' : 'text-blue-500'}`}>
                             {student.discountPerc}%
                         </div>
                     </div>
                 </div>
                 <div className="col-12 md:col-4">
                     <div className="surface-100 p-3 border-round text-center h-full">
                         <div className="text-gray-600 mb-2">Class Average</div>
                         <div className="text-2xl font-bold text-gray-700">
                             {student.classAvg}%
                         </div>
                     </div>
                 </div>
                 <div className="col-12 md:col-4">
                     <div className="surface-100 p-3 border-round text-center h-full">
                         <div className="text-gray-600 mb-2">School Average</div>
                         <div className="text-2xl font-bold text-gray-700">
                             {student.schoolAvg}%
                         </div>
                     </div>
                 </div>

                 {student.isOutlier && (
                     <div className="col-12 mt-3">
                         <div className="bg-red-50 border-left-3 border-red-500 p-3">
                             <div className="flex align-items-center mb-2">
                                 <i className="pi pi-exclamation-triangle text-red-500 mr-2" style={{fontSize: '1.2rem'}}></i>
                                 <span className="font-bold text-red-700">High Risk Disparity Flagged</span>
                             </div>
                             <p className="m-0 text-red-600 line-height-3">
                                 This student's discount ({student.discountPerc}%) is significantly higher than the class average of {student.classAvg}%. 
                                 This deviation has been flagged as an outlier and may require management review.
                             </p>
                         </div>
                     </div>
                 )}
                 
                 <div className="col-12 mt-4 text-right">
                      <Button label="View Ledger" icon="pi pi-external-link" onClick={() => this.navigateToLedger(student.studentId)} />
                 </div>
             </div>
        );
    }

    render() {
        const { 
            loading, schoolAvg, studentData, outlierCount, 
            globalFilter, selectedColumns, fixedColumns, classMetrics,
            showAnalysisDialog, selectedStudent, dropdownsData
        } = this.state;
        const header = this.renderHeader();
        
        // Dynamically create columns based on selection
        const columnComponents = selectedColumns.map(col => {
            const isPercent = ['discountPerc', 'classAvg', 'schoolAvg'].includes(col.field);
            const isCurrency = ['baseFee', 'netFee'].includes(col.field);
            
            let bodyTemplate = null;
            if (isPercent) bodyTemplate = (r) => this.percentageBody(r, col.field);
            else if (isCurrency) bodyTemplate = (r) => this.currencyBody(r, col.field);

            return (
                <Column 
                    key={col.field} 
                    field={col.field} 
                    header={col.header} 
                    sortable 
                    body={bodyTemplate}
                    style={{ minWidth: '120px' }}
                />
            );
        });

        return (
            <div className="discount-context-container">
                <Toast ref={this.toast} position="bottom-right" />
                
                <div className="card mb-3 p-3 surface-50">
                    <div className="grid align-items-end">
                        <div className="col-12 md:col-10">
                            <label className="block mb-2 font-bold text-gray-700">Filter Analysis by Cohort</label>
                            <BranchToSectionDD key={this.state.filterKey} setValues={this.setDropdownValues} />
                        </div>
                        <div className="col-12 md:col-2 text-right">
                            <Button 
                                label="Clear Filters" 
                                icon="pi pi-filter-slash" 
                                className="p-button-outlined p-button-secondary w-full" 
                                onClick={this.clearAllFilters}
                                tooltip="Clear all filters and show all records"
                                tooltipOptions={{ position: 'top' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="grid">
                    <div className="col-12 md:col-6">
                        <Card className="metric-card">
                            <div className="text-center">
                                <h3>{(dropdownsData.branchId || dropdownsData.boardId || dropdownsData.classId || dropdownsData.sectionId) ? 'Filtered' : 'School'} Average Discount</h3>
                                <h1 className="text-primary">{Number(schoolAvg || 0).toFixed(2)}%</h1>
                                {(dropdownsData.branchId || dropdownsData.boardId || dropdownsData.classId || dropdownsData.sectionId) && <small className="text-gray-500">Filtered View</small>}
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-6">
                        <Card className="metric-card">
                            <div className="text-center">
                                <h3>{(dropdownsData.branchId || dropdownsData.boardId || dropdownsData.classId || dropdownsData.sectionId) ? 'Filtered' : 'School'} Outliers Identified</h3>
                                <h1 className="text-danger">{outlierCount}</h1>
                                <small className="text-gray-500">Students with unusually high discounts</small>
                            </div>
                        </Card>
                    </div>
                </div>

                <div className="grid mt-3">
                    <div className="col-12">
                        <Card title="Detailed Discount Disparity Analysis">
                            <DataTable 
                                ref={this.dt}
                                value={studentData} 
                                paginator 
                                rows={10}
                                header={header}
                                globalFilter={globalFilter}
                                globalFilterFields={['studentName', 'admissionNo', 'className']}
                                emptyMessage="No student records found."
                                rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                                showGridlines
                                className="p-datatable-sm"
                            >
                                <Column header="Sl.No" body={(d, opts) => opts.rowIndex + 1} style={{ width:'3rem' }} />
                                {fixedColumns.map(col => (
                                    <Column key={col.field} field={col.field} header={col.header} sortable />
                                ))}
                                {columnComponents}
                                <Column body={this.actionBody} header="Analysis" style={{ width: '8rem', textAlign: 'center' }} />
                            </DataTable>
                        </Card>
                    </div>
                </div>
                
                <Dialog 
                    header="Student Discount Context" 
                    visible={showAnalysisDialog} 
                    style={{ width: '50vw' }} 
                    onHide={() => this.setState({ showAnalysisDialog: false })}
                    blockScroll
                >
                    {this.renderComparisonCard(selectedStudent)}
                </Dialog>

                {loading && <LoadingComponent />}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    userInfo: state.currentUserPerms ? state.currentUserPerms.userInfo : {}
});

const DiscountContextWithRouter = (props) => {
    const navigate = useNavigate();
    return <DiscountContext {...props} navigate={navigate} />;
};

export default connect(mapStateToProps)(DiscountContextWithRouter);
